'use client'

import { useCallback, useState } from 'react'

interface UseTooltipPortalContainerProps {
    open?: boolean
    defaultOpen?: boolean
    onOpenChange?: (open: boolean) => void
}

export interface TooltipPortalContainer {
    /** Whether the panel is open, whichever side controls it. */
    isOpen: boolean
    /** Hand to the Radix root; keeps the uncontrolled state and forwards to the caller. */
    handleOpenChange: (open: boolean) => void
    /** The mounted container, or `null` while the panel is closed or the container not yet mounted. */
    container: HTMLDivElement | null
    /** Ref for the container element. */
    setContainer: (node: HTMLDivElement | null) => void
}

/**
 * Open state and portal container for a floating panel that must never be clipped or covered.
 *
 * The panel is portalled into a `TopLayerPanel` rendered next to the trigger, and only while the
 * panel is open. The Popover API puts that container in the browser top layer, so the panel paints
 * above a modal `<dialog>` and escapes the dialog's `overflow: hidden` and `transform`, which would
 * otherwise clip a panel too tall for a short modal. Being a sibling of the trigger keeps the
 * container inside the dialog's subtree, the one place a modal leaves interactive; a container
 * rendered outside it would paint above the modal but be inert. Mounting it only while open puts it
 * in the top layer after any dialog opened earlier, which is what paints it on top.
 *
 * The open state is mirrored here because the container has to mount before Radix can portal into
 * it. Radix stays controlled by this hook; a caller's `open`, `defaultOpen` and `onOpenChange` keep
 * their usual meaning.
 *
 * Without Popover API support the container is a zero-size fixed element next to the trigger: the
 * panel still renders inside the dialog, above it, and is clipped only when the modal is too short.
 */
export const useTooltipPortalContainer = ({
    open,
    defaultOpen = false,
    onOpenChange,
}: UseTooltipPortalContainerProps): TooltipPortalContainer => {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const [container, setContainer] = useState<HTMLDivElement | null>(null)
    const isOpen = open ?? uncontrolledOpen

    const handleOpenChange = useCallback(
        (next: boolean) => {
            setUncontrolledOpen(next)
            onOpenChange?.(next)
        },
        [onOpenChange],
    )

    return { isOpen, handleOpenChange, container, setContainer }
}
